import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { User } from "oidc-client";
import { useDispatch } from "react-redux";
import userManager from "./userManager";
import { setOidcUser, setOidcLoading } from "../redux/reducers/OidcAuthReducer";

interface AuthContextValue {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    signinRedirect: (returnUrl?: string) => Promise<void>;
    completeSignIn: (url?: string) => Promise<string | void>;
    signoutRedirect: (returnUrl?: string) => Promise<void>;
    completeSignOut: (url?: string) => Promise<void>;
    getAccessToken: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch();
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const syncUser = useCallback((u: User | null) => {
        setUser(u);
        dispatch(setOidcUser(u));
        dispatch(setOidcLoading(false));
    }, [dispatch]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const current = await userManager.getUser();
                if (mounted) {
                    syncUser(current && !current.expired ? current : null);
                    setIsLoading(false);
                }
            } catch (err) {
                console.error("Auth: failed to load user", err);
                if (mounted) {
                    syncUser(null);
                    setIsLoading(false);
                }
            }
        };
        load();

        const onUserLoaded = (u: User) => syncUser(u);
        const onUserUnloaded = () => syncUser(null);
        const onAccessTokenExpired = async () => {
            // Silent renew (prompt=none) está roto en el servidor (devuelve /home/error 500).
            // Vamos directo al redirect interactivo en lugar de intentar el iframe oculto.
            try {
                await userManager.signinRedirect();
            } catch (redirectError) {
                console.error("Auth: signinRedirect failed", redirectError);
                syncUser(null);
            }
        };

        userManager.events.addUserLoaded(onUserLoaded);
        userManager.events.addUserUnloaded(onUserUnloaded);
        userManager.events.addAccessTokenExpired(onAccessTokenExpired);

        return () => {
            mounted = false;
            userManager.events.removeUserLoaded(onUserLoaded);
            userManager.events.removeUserUnloaded(onUserUnloaded);
            userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
        };
    }, [syncUser]);

    const signinRedirect = useCallback(async (returnUrl?: string) => {
        const state = returnUrl ? { returnUrl } : undefined;
        await userManager.signinRedirect({ state });
    }, []);

    const completeSignIn = useCallback(async (url?: string) => {
        const user = await userManager.signinCallback(url);
        syncUser(user || null);
        const state = (user && (user as any).state) || undefined;
        return state?.returnUrl as string | undefined;
    }, [syncUser]);

    const signoutRedirect = useCallback(async (returnUrl?: string) => {
        const state = returnUrl ? { returnUrl } : undefined;
        await userManager.signoutRedirect({ state });
    }, []);

    const completeSignOut = useCallback(async (url?: string) => {
        await userManager.signoutCallback(url);
        syncUser(null);
    }, [syncUser]);

    const getAccessToken = useCallback(async () => {
        const current = user || await userManager.getUser();
        return current?.access_token;
    }, [user]);

    const value = useMemo<AuthContextValue>(() => ({
        user,
        isAuthenticated: !!user && !user.expired,
        isLoading,
        signinRedirect,
        completeSignIn,
        signoutRedirect,
        completeSignOut,
        getAccessToken
    }), [user, isLoading, signinRedirect, completeSignIn, signoutRedirect, completeSignOut, getAccessToken]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return ctx;
};
